'use client';

import { useCallback, useState } from 'react';
import { useLocale } from 'next-intl';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { purchaseService } from '@/features/payment/api/purchase';
import { projectsService } from '@/features/project/api/projects';
import {
  isCheckoutAvailableForDescriptor,
  openCheckoutForDescriptor,
  toPaddleLocale,
} from '@/shared/lib/paddle';
import { isWebPaymentsEnabled } from '@/shared/lib/web-payments';
import {
  findPackProduct,
  useStoreCatalog,
} from '@/shared/api/hooks/use-store-catalog';
import { useUser } from '@/features/auth/model/auth.selectors';
import { readGeoCountry } from '@/shared/lib/geo-country';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import {
  AnalyticsEvent,
  trackEvent,
  PurchaseCheckoutOutcome,
  PurchaseProductKind,
} from '@/shared/lib/analytics';
import {
  trackEcommerceCheckoutOpened,
  trackEcommercePurchase,
} from '@/shared/lib/metrica-ecommerce';
import { reportCheckoutOutcome } from '@/features/payment/lib/purchase-telemetry';
import { pollUntilGranted } from '@/features/payment/lib/grant-poll';

/**
 * After checkout completes, poll the building until its Kit grant lands, then
 * invalidate so mounted UI reflects it. Fire-and-forget; uses the stable
 * QueryClient (not React state), so it's safe past unmount. This only
 * ACCELERATES the flip — the WS `user/updated pack_purchase` event and the
 * reconcile layers (L1/L2/L3) remain the authoritative backstop if the grant
 * takes longer than this window or the poll errors out.
 */
async function pollUntilPackGranted(
  queryClient: QueryClient,
  projectId: string
): Promise<void> {
  const granted = await pollUntilGranted(async () => {
    const res = await projectsService.get(projectId);
    return res.data.attributes.pack?.is_active === true;
  });

  if (!granted) {
    return;
  }

  // Grant landed — refresh the building list + render gate so the Kit tag
  // flips and Generate unlocks. Mirrors the WS pack_purchase reconcile.
  queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
  queryClient.invalidateQueries({ queryKey: cacheKeys.queueStatus.all });
  // The storefront catalog carries the building's `has_active_pack` +
  // pack `is_additional` state; drop it so the just-bought Kit stops
  // showing the buy CTA (it caches for 5min otherwise).
  queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
}

/**
 * Web Facade Kit checkout: create the correlation intent, then open the Paddle
 * overlay bound to the building. The API webhook owns the grant; the
 * `checkout.completed` event only refetches /projects locally so the building's
 * `pack.is_active` flips without waiting on the webhook round-trip (the WS
 * `user/updated` `pack_purchase` reconcile covers other tabs/devices). No
 * client-side payment-state interpretation (API-driven state rule).
 */
/**
 * Host callbacks the paywall wires in to keep its overlay clickable and show
 * the post-purchase success state in place (parity with Apple/Android, which
 * stay open on the celebration):
 *  - `onPurchased` — checkout.completed: flip the paywall body to its success
 *    celebration instead of closing it.
 *  - `onClosed` — checkout.closed: the overlay went away (purchase OR cancel),
 *    so the host can restore its modal lock.
 */
export interface PackCheckoutCallbacks {
  onPurchased?: () => void;
  onClosed?: () => void;
}

export function usePackCheckout(
  projectId: string | undefined,
  callbacks?: PackCheckoutCallbacks
) {
  const queryClient = useQueryClient();
  const user = useUser();
  const locale = useLocale();
  const [isLaunching, setIsLaunching] = useState(false);
  const onPurchased = callbacks?.onPurchased;
  const onClosed = callbacks?.onClosed;

  // The Kit's price id comes from the server `checkout` descriptor on the
  // /store catalog item, NEVER from a build-time env (frontends must not
  // hardcode Paddle price_ids). The descriptor is also the region gate: the
  // API ships `checkout: null` when Paddle cannot sell to this visitor
  // (RU/BY — the overlay would dead-end with E-403), which must kill the buy
  // pill here exactly like it does for passes and token packages.
  const { data: catalog } = useStoreCatalog({
    // The catalog IS the region/provider gate (per-product `checkout`
    // descriptor: Paddle, tbank, or null) — fetch it whenever web payments are
    // on at all, not only when the Paddle client is configured, or the tbank
    // rail could never surface.
    enabled: isWebPaymentsEnabled && !!projectId,
  });
  const packProduct = findPackProduct(catalog);
  const descriptor = packProduct?.attributes.checkout ?? null;
  // Secondary rail (T-Bank, RF-issued cards) for RU-locale buyers on the
  // Paddle rail — the CTA renders it under the primary button.
  const alternative = packProduct?.attributes.checkout_alternative ?? null;

  const available = !!projectId && isCheckoutAvailableForDescriptor(descriptor);

  const startCheckout = useCallback(async () => {
    if (!projectId || !descriptor) {
      return;
    }

    setIsLaunching(true);
    try {
      trackEvent(AnalyticsEvent.PACK_PURCHASE_STARTED, { project_bound: true });
      trackEcommerceCheckoutOpened('pack', 'pack');

      // T-Bank: server mints the intent inside the checkout-session call and
      // we leave the page for the bank-hosted form — no overlay listeners, no
      // grant poll (the return page + notification webhook own the rest).
      if (descriptor.provider === 'tbank') {
        await openCheckoutForDescriptor(descriptor, {
          kind: 'pack',
          projectId,
        });
        return;
      }

      const intent = await purchaseService.createPackIntent(projectId);

      // Paddle fires `closed` for BOTH a completed purchase and an abandoned
      // one, so completion has to be latched to tell them apart. Without this
      // every successful checkout would also report itself as cancelled.
      let completed = false;

      await openCheckoutForDescriptor(descriptor, {
        kind: 'pack',
        correlationToken: intent.correlation_token,
        // Prefill buyer email (always known — logged-in) + live geo country so
        // Paddle skips its "Your details" step. Country comes from the geo
        // header (not the stored profile) so it tracks the buyer's actual
        // location; Paddle still owns final tax based on payment + location.
        customerEmail: user?.email || undefined,
        countryCode: readGeoCountry(),
        // Match the checkout UI language to the app's active locale (Paddle
        // otherwise defaults to browser language / English regardless of UI).
        locale: toPaddleLocale(locale),
        onCompleted: () => {
          completed = true;
          trackEcommercePurchase('pack', 'pack', intent.correlation_token);
          // Optimistic immediate refresh — covers the fast path where the grant
          // already committed by the time the JS event fires.
          queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
          queryClient.invalidateQueries({
            queryKey: cacheKeys.queueStatus.all,
          });
          // Refresh the storefront catalog so the bought Kit's
          // `has_active_pack` / `is_additional` ownership flips immediately.
          queryClient.invalidateQueries({
            queryKey: cacheKeys.storeCatalog.all,
          });
          // Then poll the building until the async webhook grant lands, so the
          // Kit tag flips in-moment without a manual refresh. WS
          // `user/updated pack_purchase` + reconcile remain the backstop.
          void pollUntilPackGranted(queryClient, projectId);
          // Let the host paywall flip to its success celebration in place.
          onPurchased?.();
        },
        onClosed: () => {
          if (!completed) {
            reportCheckoutOutcome(
              PurchaseCheckoutOutcome.USER_CANCELLED,
              PurchaseProductKind.PACK,
              undefined,
              intent.correlation_token,
            );
          }
          onClosed?.();
        },
      });
    } catch (error) {
      // Surface the server-provided failure (API-driven message) — without
      // this a failed T-Bank session / intent mint dies as an unhandled
      // rejection and the buy button just "does nothing".
      if (error instanceof ApiClientError) {
        // The intent may not exist yet (this IS the mint failing, or the
        // T-Bank session call), so the report carries no token — GA4 still
        // keeps the data point.
        reportCheckoutOutcome(
          PurchaseCheckoutOutcome.CLIENT_ERROR,
          PurchaseProductKind.PACK,
          `api_${error.status ?? 'error'}`,
          undefined,
        );
        toast.error(error.message);
        return;
      }
      reportCheckoutOutcome(
        PurchaseCheckoutOutcome.CLIENT_ERROR,
        PurchaseProductKind.PACK,
        'unexpected',
        undefined,
      );
      throw error;
    } finally {
      setIsLaunching(false);
    }
  }, [projectId, descriptor, queryClient, user?.email, locale, onPurchased, onClosed]);

  return { available, descriptor, alternative, isLaunching, startCheckout };
}
